'use client';

import { Switch } from '@savvy-kit/components/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import {
  HIDEABLE_OVERVIEW_SECTION_KEYS,
  overviewSectionMeta,
  RELATIONSHIP_PANEL_KEY,
} from 'src/components/advisor-dashboard/account-overview/overview-panels/overview-section-keys';

import { SortableRowList } from './sortable-row-list';

export type OverviewDraft = {
  readonly order: readonly string[];
  /** Keyed by `household`/`contacts`/`portfolio` only — Relationship can never appear here. */
  readonly hidden: Readonly<Record<string, boolean>>;
};

type OverviewPaneProps = {
  readonly draft: OverviewDraft;
  readonly onChange: (next: OverviewDraft) => void;
};

const FLOOR_REASON = 'At least one of Household, Contacts, or Portfolio must stay visible.';

export function OverviewPane({ draft, onChange }: OverviewPaneProps) {
  const visibleHideableCount = HIDEABLE_OVERVIEW_SECTION_KEYS.filter((key) => draft.hidden[key] !== true).length;

  return (
    <div className="pt-1">
      <p className="mb-1 px-0.5 text-xs text-secondary">Drag to reorder, or toggle a section off.</p>
      <SortableRowList
        order={draft.order}
        onReorder={(next) => onChange({ ...draft, order: next })}
        labelFor={(id) => overviewSectionMeta(id)?.title ?? id}
        descriptionFor={(id) => overviewSectionMeta(id)?.description}
        iconFor={(id) => overviewSectionMeta(id)?.icon}
        rowClassName="py-3"
        trailingFor={(id) => {
          const label = overviewSectionMeta(id)?.title ?? id;

          if (id === RELATIONSHIP_PANEL_KEY) {
            return <span className="shrink-0 text-xs text-tertiary">Always shown</span>;
          }

          const isHidden = draft.hidden[id] === true;
          const isFloorLocked = !isHidden && visibleHideableCount <= 1;
          const toggle = (
            <Switch
              checked={!isHidden}
              onCheckedChange={(checked) => onChange({ ...draft, hidden: { ...draft.hidden, [id]: !checked } })}
              disabled={isFloorLocked}
              // The reason rides on the switch's own name because the tooltip is the only other
              // place it lives, and a disabled control can't be focused to open one.
              aria-label={isFloorLocked ? `Show ${label} — ${FLOOR_REASON}` : `Show ${label}`}
            />
          );

          if (!isFloorLocked) return toggle;

          return (
            <Tooltip>
              {/* A disabled control doesn't reliably fire hover, so the trigger is a focusable
                  wrapper around it. No `role` on the wrapper: `role="button"` makes the Switch a
                  presentational child, dropping its role and checked state from the a11y tree. */}
              <TooltipTrigger render={<span tabIndex={0} className="inline-flex cursor-help" />}>
                {toggle}
              </TooltipTrigger>
              <TooltipContent side="top">{FLOOR_REASON}</TooltipContent>
            </Tooltip>
          );
        }}
      />
    </div>
  );
}
